import { FormikProvider, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import { getIncidentFormValidator } from "../../../screens/User/Users/components/logIncident/validator";
import { useStoreWrapper, useViewStateWrapper } from "../../../store/store";
import {
  TabOptions,
  IFieldInstance,
  IncidentStatus,
} from "../../../types/fieldInstance.interface";
import { getSectionSeparatedData } from "../../../utils/formGenUtils/getSectionSeparatedData";
import { getViewModel } from "../../../utils/formGenUtils/viewModel";
import { stages } from "../../../utils/helpers/incidentFormTabs";
import useSelectOptions from "../../../hooks/useSelectOptions";
import {
  getRLValues,
  incidentRelatedLists,
} from "../../../utils/helpers/relatedListHelpers";
import { getAllRLViewModels } from "../../../utils/formGenUtils/rLViewModel";
import useFormValidation from "../../../hooks/useFormValidation";
import { getFacilityEditPermissions } from "../../../utils/formGenUtils/getFacilityPermissions";
import { getDefaultValues } from "../../../utils/formGenUtils/getDefaultValues";
import { getFormBasicDetails } from "../../../utils/formGenUtils/getFormBasicDetails";
import { useIncidentData } from "../../../layouts/incidentLayout";
import { defaultState, submitIncidentForm } from "./methodsAndHandlers";
import EMRReminderModal from "./EMRReminderModal";
import CustomLoader from "../../CustomLoader";
import ErrorMessage from "./ErrorMessage";
import { IncidentContextProvider } from "./IncidentContext";
import IncidentFormComponent from "./IncidentFormComponent";
import useModeLock from "../../../hooks/useModeLock";
interface IProps {
  tabTitle: Exclude<TabOptions, TabOptions.Approvals>;
}

const IncidentForm = ({ tabTitle }: IProps) => {
  const { forms, userId, userName, userFacilities, resetFormState } =
    useStoreWrapper(
      (state) => ({
        forms: state.forms,
        userId: state.user.userId,
        userName: `${state.user.firstName} ${state.user.lastName}`,
        userFacilities: state.userFacilities,
        resetFormState: state.resetForm,
      }),
      shallow
    );

  const { incidentIdOrNew: incidentID } = useParams();
  const formId = incidentID || "";
  const {
    baseFormData: {
      facilityId,
      residentId,
      incidentType,
      guestName,
      ssnLastFourDigits,
      agency,
      shiftWorked,
      unitAssignment,
    },
    incidentFormData,
    formState: {
      incidentStatus,
      tabState,
      hasUnsavedChanges,
      markedAsHighSeverity,
      markedHighSeverityInSession,
      shouldResetForm,
      showPrompt,
      clickedStage,
      exitLink,
    },
    deleteRelatedRecords,
  } = forms[formId] || defaultState;
  const [searchParams, setSearchParams] = useSearchParams();
  const editModeActive = searchParams.get("mode") === "edit";

  const {
    tabSeparatedData,
    initialValues,
    allFields,
    createIncidentLoading,
    createMutateAsync,
    submitSuccessCount,
    selectedIncident,
    isIncidentLoading,
    isDataError,
    incidentTypeObj,
    setIncidentFormData,
    setIncidentFormState,
    mergeIncidentTabState,
    setDeleteRLRecordObject,
    resetDeleteRLRecordObject,
  } = useIncidentData();
  const [viewModel, setViewModel] = useState(
    //@ts-ignore
    getViewModel(
      initialValues,
      incidentType,
      tabTitle,
      undefined,
      undefined,
      allFields
    )
  );

  const [rlViewModel, setRlViewModel] = useState(
    getAllRLViewModels(incidentRelatedLists, initialValues)
  );

  const [_rlValues, _setRlValues] = useState();

  const [yupValidationSchema, setYupValidationSchema] = useState<any>();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [_canSubmitForm, setCanSubmitForm] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [shouldShowLoader, setShouldShowLoader] = useState(false);

  const [formValues, setFormValues] = useState<any>(null);

  const [shouldRenderNull, setShouldRenderNull] = useState(false);

  const [shouldStayOnPage, setShouldStayOnPage] = useState(false);

  const [shouldUpdateForm, setShouldUpdateForm] = useState(false);

  const [showConfirmationPrompt, setShowConfirmationPrompt] = useState(false);

  const [showHighSeverityModal, setShowHighSeverityModal] = useState(false);

  const [highSeverityAcknowledged, setHighSeverityAcknowledged] =
    useState(false);

  const [tempValue, setTempValue] = useState(false);

  const [showEMRReminder, setShowEMRReminder] = useState(false);

  const [pendingNavigation, setPendingNavigation] = useState<{
    values: FormValues;
    previousRoute: string | null;
    shouldResetForm: boolean;
    resetForm: (values: FormValues) => void;
    navRoute?: string;
  } | null>(null);

  const isInitialValuesInvalid = useRef(
    !formValues || !Object.keys(formValues).length
  );

  // Only validate the whole form on initial load, not on tab remounts.
  // If any tab is already 'visited', full validation has run before.
  const hasTabBeenValidated = tabState
    ? Object.values(tabState).some((tab: any) => tab?.visited)
    : false;
  const shouldValidateWholeForm = useRef(!hasTabBeenValidated);

  const formBasicDetails = getFormBasicDetails(
    facilityId,
    residentId,
    selectedIncident,
    userFacilities
  );

  useEffect(() => {
    isInitialValuesInvalid.current =
      !formValues || !Object.keys(formValues).length;
  }, [formValues]);

  // Trigger shouldUpdateForm only when the incident Id actually changes, not on tab remounts.
  const prevSelectedIncidentIdRef = useRef<string | undefined>(
    selectedIncident?.Id
  );

  useEffect(() => {
    const currentId = selectedIncident?.Id;
    if (currentId && currentId !== prevSelectedIncidentIdRef.current) {
      setShouldUpdateForm(true);
      prevSelectedIncidentIdRef.current = currentId;
    }
  }, [selectedIncident?.Id]);

  // On resubmit the Id doesn't change, so snapshot selectedIncident on each
  // successful submit and wait for the refetch to produce a new reference.
  const prevSubmitCountRef = useRef(submitSuccessCount);
  const postSubmitIncidentRef = useRef<any>(null);

  useEffect(() => {
    if (submitSuccessCount > prevSubmitCountRef.current) {
      postSubmitIncidentRef.current = selectedIncident;
    }
    prevSubmitCountRef.current = submitSuccessCount;
  }, [submitSuccessCount, selectedIncident]);

  useEffect(() => {
    const shouldGetFormValues = incidentStatus
      ? !selectedIncident || isInitialValuesInvalid.current
      : isInitialValuesInvalid.current;

    // Post-submit refetch has arrived when selectedIncident is a new reference.
    const isPostSubmitRefresh =
      postSubmitIncidentRef.current !== null &&
      selectedIncident !== postSubmitIncidentRef.current;
    if (isPostSubmitRefresh) {
      postSubmitIncidentRef.current = null;
    }

    const effectiveShouldUpdate = shouldUpdateForm || isPostSubmitRefresh;

    // On load, server wins over a stale cache when viewing a non-draft incident;
    // gate on view mode (not race-prone hasUnsavedChanges) so edits aren't clobbered.
    // Tradeoff: a stale cache also survives a refresh while in edit mode (accepted).
    const isNonDraftIncident =
      !!incidentStatus && incidentStatus !== IncidentStatus.Draft;
    const isInitialFormLoad =
      shouldGetFormValues &&
      !!selectedIncident &&
      (!incidentFormData || (isNonDraftIncident && !editModeActive));
    const shouldPrioritizeAPI = effectiveShouldUpdate || isInitialFormLoad;

    if (shouldGetFormValues || effectiveShouldUpdate) {
      const formValues = getDefaultValues(
        incidentStatus,
        selectedIncident,
        initialValues,
        incidentFormData,
        formBasicDetails,
        deleteRelatedRecords,
        shouldPrioritizeAPI
      );
      setFormValues(formValues);
      isInitialValuesInvalid.current = false;
      setShouldUpdateForm(false);

      // Sync Zustand with fresh API values to clear any stale RL records.
      if (isPostSubmitRefresh || isInitialFormLoad) {
        setIncidentFormData(formValues);
      }
    }
  }, [
    selectedIncident,
    incidentFormData,
    incidentStatus,
    deleteRelatedRecords,
    formBasicDetails,
    initialValues,
    shouldUpdateForm,
  ]);

  const tabFields = tabSeparatedData[tabTitle];
  const sections = useMemo(
    () => getSectionSeparatedData(tabFields),
    [tabTitle]
  );
  const currentStageIndex = stages.findIndex(
    (stage) => stage.label === tabTitle
  );
  const _stages =
    incidentStatus && incidentStatus !== IncidentStatus.Draft
      ? stages.filter((stage) => !stage.hideInSection)
      : stages.filter(
          (stage) => !stage.hideInSection && !stage.renderAfterSubmit
        );
  const isDeletedIncident =
    selectedIncident?.Enabled_Incident_For_Web_User__c === false;
  const isLastStep = currentStageIndex === _stages.length - 1;
  const allowInput = incidentStatus ? editModeActive : true;
  const navigate = useNavigate();
  const location = useLocation();
  const incidentReturnTo = useViewStateWrapper((state) => state.incidentReturnTo);
  const previousRoute = location?.state?.from || null;
  const incidentTypeFlag = incidentTypeObj?.flag;

  function _handleDiscard(path?: string) {
    new Promise<void>((resolve) => {
      setIncidentFormState({ shouldResetForm: true });
      resolve();
    }).then(() => {
      let routeTo;
      routeTo = incidentReturnTo ?? "/user/incidents-list";
      navigate(path ?? routeTo);
    });
  }

  function _navigateWithinForm(path: string) {
    const pathSegments = location.pathname.split("/");
    const nextPathSegments = path.split("/");
    pathSegments.pop();
    const nextPath = nextPathSegments.pop() ?? "";
    pathSegments.push(nextPath);
    const currentSearchParams = location.search;
    const newPath = `${pathSegments.join("/")}${currentSearchParams}`;
    navigate(newPath);
  }

  const { canUserEditPostSubmission, editPermission } =
    getFacilityEditPermissions(
      userFacilities,
      facilityId,
      incidentStatus,
      userId,
      selectedIncident?.Incident_Created_By__c
    );

  const formStateValues = {
    guestName,
    ssnLastFourDigits,
    agency,
    shiftWorked,
    unitAssignment,
    incidentTypeFlag,
    userId,
    userName,
    incidentCreatedBy: undefined,
    initialValues: undefined,
    deleteRelatedRecords: [],
    markedAsHighSeverity,
  };

  const formSubmitStates = {
    createIncidentLoading,
    isLastStep,
    canSubmitForm: _canSubmitForm,
    editModeActive,
    previousRoute,
    isDraft: incidentStatus === IncidentStatus.Draft,
  };

  const allFormFields = {
    allFields,
    incidentRelatedLists,
  };

  function onSuccess(
    values: any,
    previousRoute: string | null,
    shouldResetForm: boolean,
    resetForm: (values: any) => void,
    resetFormState: (formState: any, setFormState: (x: any) => void) => void,
    navRoute?: string
  ) {
    const pendingData = {
      values,
      previousRoute,
      shouldResetForm,
      resetForm,
      navRoute,
    };

    // Show EMR reminder on initial submission or when submitting a draft,
    // but not when editing/resubmitting an already-submitted incident
    const isInitialSubmission = !values.Id && !incidentStatus;
    const wasDraftBeforeSubmission =
      values.Incident_Status__c === IncidentStatus.Draft ||
      incidentStatus === IncidentStatus.Draft;
    const isEditingSubmittedIncident =
      values.Incident_Status__c === IncidentStatus.Submitted ||
      values.Incident_Status__c === IncidentStatus.Open;
    const shouldShowEMR =
      (isInitialSubmission || wasDraftBeforeSubmission) &&
      !isEditingSubmittedIncident;

    if (shouldShowEMR) {
      setPendingNavigation(pendingData);
      setShowEMRReminder(true);
    } else {
      // For edits/resubmissions, skip EMR — call processFormCompletion directly
      // with the local variable to avoid the async state issue where
      // pendingNavigation would still be null when handleEMRAcknowledgement reads it
      processFormCompletion(pendingData);
    }
  }

  // Process form completion - called either after EMR acknowledgement or directly when EMR is skipped
  function processFormCompletion(data: typeof pendingNavigation) {
    if (!data) return;

    const { values, previousRoute, shouldResetForm, resetForm, navRoute } = data;

    if (incidentStatus === IncidentStatus.Submitted) {
      // Clear each tab's dirty flag via per-tab merge (not a full-tabState
      // replace) so a parallel validity write isn't clobbered on resubmit.
      const dirtyReset = Object.keys(tabState).reduce((acc: any, key) => {
        acc[key] = { isFormDirty: false };
        return acc;
      }, {});
      mergeIncidentTabState(dirtyReset);
      setIncidentFormState({ hasUnsavedChanges: false });
      
      if (shouldResetForm) {
        resetForm({ values });
        setIncidentFormData(values);
      }
      resetDeleteRLRecordObject();
      setIncidentFormState({ markedHighSeverityInSession: false });
      setHighSeverityAcknowledged(false);
      
      // Check if we should redirect to approvals page
      const hasConclusion = !!values.Conclusion__c?.trim();
      const incidentId = selectedIncident?.Id;
      if (incidentId && hasConclusion) {
        navigate(`/incidents/${incidentId}/approvals`);
      } else {
        previousRoute && _navigateWithinForm(previousRoute);
      }
    } else {
      navRoute ? _handleDiscard(navRoute) : _handleDiscard();
    }
  }

  function handleEMRAcknowledgement() {
    if (!pendingNavigation) return;
    processFormCompletion(pendingNavigation);
    setPendingNavigation(null);
  }

  type FormValues = { [key: string]: any };

  const formik = useFormik<FormValues>({
    initialValues: formValues || {},
    validationSchema: yupValidationSchema || null,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (markedHighSeverityInSession && !highSeverityAcknowledged) {
        setShowHighSeverityModal(true);
        return;
      }
      try {
        const res = await submitIncidentForm(
          values,
          {
            ...formStateValues,
            initialValues: initialValues,
            deleteRelatedRecords,
          },
          formSubmitStates,
          resetForm,
          incidentFormData,
          allFormFields,
          createMutateAsync,
          onSuccess
        );
        return res;
      } catch (error) {
        // log error
        return error;
      }
    },
  });

  useEffect(() => {
    //@ts-ignore
    setViewModel(
      getViewModel(
        formik.values,
        incidentType,
        tabTitle,
        undefined,
        undefined,
        allFields
      )
    );
    const newRLValues = getRLValues(
      formik.values,
      Object.values(incidentRelatedLists)
    );
    _setRlValues(newRLValues);
    setRlViewModel(getAllRLViewModels(incidentRelatedLists, formik.values));
  }, [formik.values]);

  const { canSubmitForm } = useFormValidation(
    formik,
    tabSeparatedData,
    viewModel,
    rlViewModel,
    // @ts-ignore
    tabTitle,
    formId,
    shouldValidateWholeForm
  );

  useEffect(() => {
    setCanSubmitForm(canSubmitForm);
  }, [canSubmitForm]);

  useEffect(() => {
    const validationSchema = allowInput
      ? getIncidentFormValidator(
          tabFields as IFieldInstance[],
          viewModel,
          rlViewModel,
          !!incidentStatus && incidentStatus !== IncidentStatus.Draft
        )
      : null;
    setYupValidationSchema(validationSchema);
  }, [_rlValues]);

  const selectOptions = useSelectOptions(facilityId);
  const { errorGettingOptions, optionsLoading } = selectOptions;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue =
          "Are you sure you want to reload? Changes you made may not be saved.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      if (!shouldResetForm && forms[formId]) {
        setIncidentFormData(formik.values);
        setIncidentFormState({ clickedStage: "" });
      }
    };
  }, [clickedStage, shouldResetForm]);

  useEffect(() => {
    return () => {
      if (shouldResetForm) {
        resetFormState(formId);
      }
    };
  }, [shouldResetForm]);

  useEffect(() => {
    const isResourceLoading = optionsLoading && !errorGettingOptions;
    if (incidentStatus) {
      setShouldShowLoader(
        isIncidentLoading ||
          isResourceLoading ||
          !formValues ||
          !Object.keys(formValues).length
      );
    } else {
      setShouldShowLoader(
        isResourceLoading || !formValues || !Object.keys(formValues).length
      );
    }
  }, [optionsLoading, errorGettingOptions, isIncidentLoading, formValues]);

  useEffect(() => {
    const _shouldRenderNull =
      !viewModel ||
      !initialValues ||
      errorGettingOptions ||
      isDataError ||
      !forms[formId];
    setShouldRenderNull(_shouldRenderNull);
  }, [
    viewModel,
    initialValues,
    errorGettingOptions,
    isDataError,
    formValues,
    forms[formId],
  ]);

  useModeLock({
    canUserEditPostSubmission,
    editModeActive,
    setSearchParams,
    incidentStatus,
  });

  function onExit() {
    navigate("/");
  }

  if (shouldRenderNull) {
    return <ErrorMessage onExit={onExit} />;
  } else {
    return shouldShowLoader ? (
      <div>
        <CustomLoader fullscreen />
      </div>
    ) : (
      <IncidentContextProvider
        value={{
          viewModel,
          rlViewModel,
          setDeleteRLRecordObject,
          selectOptions,
          canSubmitForm,
          formBasicDetails,
          shouldAllowInput: allowInput,
          editPermission,
          canUserEditPostSubmission,
          isSubmitting,
          shouldStayOnPage,
          setShouldStayOnPage,
          openDeleteModal,
          setOpenDeleteModal,
          setSearchParams,
          setIsSubmitting,
          hasUnsavedChanges,
          showPrompt,
          navigateWithinForm: _navigateWithinForm,
          isLastStep,
          currentStageIndex,
          _handleDiscard,
          formStateValues,
          allFormFields,
          tabState,
          exitLink,
          showConfirmationPrompt,
          setShowConfirmationPrompt,
          tempValue,
          setTempValue,
          shouldResetForm,
          markedAsHighSeverity,
          showHighSeverityModal,
          setShowHighSeverityModal,
          setHighSeverityAcknowledged,
          showEMRReminder,
          setShowEMRReminder,
          handleEMRAcknowledgement,
        }}
      >
        <FormikProvider value={formik}>
          <IncidentFormComponent
            incidentStatus={incidentStatus}
            tabTitle={tabTitle}
            _handleDiscard={_handleDiscard}
            sections={sections}
          />
        </FormikProvider>
      </IncidentContextProvider>
    );
  }
};

export default IncidentForm;
